import React, { useRef, useEffect } from 'react';
import { Bell, Check, ExternalLink, Calendar, MessageSquare, CheckSquare, AlertTriangle, Volume2, Video } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (url: string) => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onNavigate,
}) => {
  useEscapeKey(isOpen, onClose);
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    browserPermission,
    requestNotificationPermission,
    playSound,
  } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = (type: string) => {
    switch (type) {
      case 'task_assigned':
      case 'task_status':
        return <CheckSquare className="w-4 h-4 text-brand-500" />;
      case 'chat_message':
      case 'mention':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case 'deadline_warning':
        return <AlertTriangle className="w-4 h-4 text-rose-500" />;
      case 'meeting':
        return <Video className="w-4 h-4 text-brand-500" />;
      default:
        return <Bell className="w-4 h-4 text-surface-500" />;
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-sm bg-white dark:bg-surface-900 rounded-2xl shadow-elevated border border-surface-200 dark:border-surface-800 z-50 overflow-hidden animate-scale-up"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100 dark:border-surface-800">
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-sm text-surface-900 dark:text-surface-100">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-brand-500 text-white">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => playSound()}
            title="Test Loud Alert Chime"
            className="text-[11px] font-semibold text-surface-500 hover:text-brand-600 flex items-center"
          >
            <Volume2 className="w-3.5 h-3.5 mr-1 text-white fill-white bg-gradient-to-tr from-[#0162fc] to-[#4b2efa] rounded-full p-0.5" />
            <span>Chime</span>
          </button>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 flex items-center"
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Permission Warning Banner if not granted */}
      {browserPermission !== 'granted' && typeof window !== 'undefined' && 'Notification' in window && (
        <div className="px-3.5 py-2 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/50 flex items-center justify-between text-xs text-amber-900 dark:text-amber-200">
          <span className="text-[11px]">Enable browser desktop & sound alerts:</span>
          <button
            type="button"
            onClick={() => requestNotificationPermission()}
            className="px-2 py-0.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-[10px] shadow-sm ml-2 shrink-0"
          >
            Allow
          </button>
        </div>
      )}

      {/* Notification List */}
      <div className="max-h-[380px] overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-sm text-surface-400 dark:text-surface-500">
            No notifications yet. You're all caught up!
          </div>
        ) : (
          notifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => {
                if (!notif.isRead) markAsRead(notif.id);
                window.dispatchEvent(
                  new CustomEvent('navigate-notification', {
                    detail: {
                      type: notif.type,
                      title: notif.title,
                      linkUrl: notif.linkUrl || (notif as any).link_url,
                      channelId: (notif as any).channelId || (notif as any).channel_id,
                      taskId: (notif as any).taskId || (notif as any).task_id,
                      projectId: (notif as any).projectId || (notif as any).project_id,
                    },
                  })
                );
                onClose();
              }}
              className={`p-3.5 flex items-start space-x-3 cursor-pointer transition-colors ${
                notif.isRead
                  ? 'hover:bg-surface-50 dark:hover:bg-surface-800/40 opacity-75'
                  : 'bg-brand-50/40 dark:bg-brand-950/20 hover:bg-brand-50/70 dark:hover:bg-brand-950/40'
              }`}
            >
              <div className="mt-0.5 p-2 rounded-xl bg-surface-100 dark:bg-surface-800 shrink-0">
                {getIcon(notif.type)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-surface-900 dark:text-surface-100">
                    {notif.title}
                  </span>
                  {!notif.isRead && (
                    <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
                  )}
                </div>
                <p className="text-xs text-surface-600 dark:text-surface-400 mt-0.5 line-clamp-2">
                  {notif.message}
                </p>
                <span className="text-[10px] text-surface-400 mt-1 block">
                  {new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
